import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Button } from "@trillion/ui/components/ui/button";
import { AdminHeader } from "@trillion/ui/components/admin/admin-header";
import { PageHeader } from "@trillion/ui/components/admin/page-header";
import { InviteUserForm } from "@/components/users/invite-user-form";
import { adminApi } from "@/lib/admin-api";
import type { InviteUserRequest } from "@/types";

export function UserInvitePage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(data: InviteUserRequest) {
    setIsLoading(true);
    try {
      const result = await adminApi.users.invite(data);
      if (result.email_sent) {
        toast.success(`Invite sent to ${result.email}.`);
      } else {
        // The user + invitation were created, but the email send failed (logged server-side).
        toast.warning(
          `User created, but the invite email to ${result.email} failed to send.`
        );
      }
      navigate("/users");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to invite user."
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="flex flex-col">
      <AdminHeader
        title="Invite User"
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Users", href: "/users" },
          { label: "Invite" },
        ]}
      />
      <div className="flex flex-col gap-6 p-6">
        <PageHeader
          title="Invite User"
          description="Pre-create a pending account and email a 24-hour join link."
          actions={
            <Button variant="outline" onClick={() => navigate("/users")}>
              <ArrowLeft className="mr-2 size-4" />
              Back to Users
            </Button>
          }
        />
        <InviteUserForm onSubmit={handleSubmit} isLoading={isLoading} />
      </div>
    </div>
  );
}
